<template>
  <div class="client-student-management-group">
    <v-row no-gutters>
      <v-col cols="4" class="parent-selection-col">
        <v-autocomplete
          ref="parentSelection"
          v-model="selectedApplyParentId"
          :items="parentSelectionList"
          item-title="group"
          item-value="id"
          placeholder="Set parent for selected group"
          clear-icon="mdi-close"
          color="primary"
          clearable
          hide-details
          single-line
          :hide-selected="hideSelected"
          :disabled="groupsLoading"
          variant="solo"
          density="compact"
        >
          <template #item="data">
            <v-list-item
              v-bind="data.props"
              :id="`client-individual-modal--set-parent-for-selected-group--option--${String(
                data.item.group
              ).replaceAll(' ', '')}`"
            >
              {{ data.item.group }}
            </v-list-item>
          </template>
        </v-autocomplete>
      </v-col>
      <v-col cols="auto" class="pl-1">
        <v-checkbox
          id="client-individual-apply-subject-checkbox"
          v-model="applySubjectEnabled"
          label="Subject"
          :disabled="groupsLoading"
          color="primary"
        />
      </v-col>
      <v-col cols="auto" class="pl-1">
        <v-checkbox
          id="client-individual-apply-custom-checkbox"
          v-model="applyCustomEnabled"
          label="Custom"
          :disabled="groupsLoading"
          color="primary"
        />
      </v-col>
      <v-col cols="auto" class="pl-1 pt-1">
        <cs-button
          id="btnApply"
          primary
          name="btnApply"
          :disabled="applyButtonDisabled"
          label="Apply"
          @click="applySelectedOption()"
        />
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="12">
        <v-col v-if="studentManagementGroupSyncDiffs.length > 0" cols="12">
          <student-group-sync-diff-alert
            :student-group-sync-diffs="studentManagementGroupSyncDiffs"
          />
        </v-col>
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="6" class="py-0">
        <v-text-field
          id="student-management-search-input"
          v-model="searchGroup"
          variant="underlined"
          density="compact"
          color="primary"
          clearable
          clear-icon="mdi-close"
          placeholder="Search Group Name"
          :disabled="groupsLoading"
          class="search-group-textfield"
          @click:clear="clearSearchInput()"
        ></v-text-field>
      </v-col>
      <v-col cols="6" class="py-0">
        <cs-button
          id="btnStudentManagementGroupSearch"
          primary
          name="btnStudentGroupSearch"
          label="Search Group"
          class="mt-0"
          :disabled="groupsLoading"
          @click="filterGroups()"
        />
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="12" class="py-0">
        <v-data-table
          v-model="selectedGroupItems"
          :headers="groupHeaders"
          :items="editableGroupList"
          :items-per-page-options="[10, 30, 50]"
          :page="studentGroupPaginationInfo.page"
          :items-per-page="studentGroupPaginationInfo.itemsPerPage"
          :sort-by="studentGroupPaginationInfo.sortBy"
          show-select
          return-object
          no-data-text="No group was found"
          height="600"
          fixed-header
          :loading="groupsLoading ? 'primary' : false"
          @update:page="studentGroupPaginationInfo.page = $event"
          @update:items-per-page="studentGroupPaginationInfo.itemsPerPage = $event"
          @update:sort-by="studentGroupPaginationInfo.sortBy = $event"
        >
          <template #[`header.data-table-select`]="{ allSelected, selectAll, someSelected }">
            <v-checkbox-btn
              :indeterminate="someSelected && !allSelected"
              :model-value="allSelected"
              color="primary"
              @update:model-value="selectAll(!allSelected)"
            ></v-checkbox-btn>
          </template>
          <template #[`item.data-table-select`]="{ item, internalItem, isSelected, toggleSelect }">
            <v-checkbox-btn
              :id="getStudentManagementCheckboxId(item.group)"
              :model-value="isSelected(internalItem)"
              color="primary"
              :disabled="groupsLoading"
              @update:model-value="toggleSelect(internalItem)"
            ></v-checkbox-btn>
          </template>

          <template #[`item.group`]="{ item }">
            {{ item.group }}
            <span v-if="vendorGroupIdDisplayEnabled" class="studentGroupId"> [{{ item.id }}]</span>
          </template>

          <template #[`item.school`]="{ item }">
            <v-select
              :model-value="getSelectedSchoolType(item)"
              :items="schoolTypes"
              clearable
              clear-icon="mdi-close"
              item-title="name"
              item-value="id"
              label="School Type"
              variant="solo"
              density="compact"
              color="primary"
              hide-details
              :disabled="groupsLoading"
              single-line
              class="school-type-select"
              @update:model-value="schoolTypeChanged(item, $event)"
            >
              <template #item="data">
                <v-list-item
                  v-bind="data.props"
                  :id="`client-individual-modal--student-management--school-type--option-${String(
                    data.item.name
                  ).replaceAll(' ', '')}`"
                >
                  {{ data.item.name }}
                </v-list-item>
              </template>
            </v-select>
          </template>
          <template #[`item.parent`]="{ item }">
            <v-autocomplete
              v-if="item.parentSelectionDisplayed"
              v-model="item.parent"
              :items="parentSelectionList"
              label="Parent"
              item-title="group"
              item-value="id"
              clearable
              clear-icon="mdi-close"
              color="primary"
              hide-details
              :hide-selected="hideSelected"
              :disabled="groupsLoading"
              variant="solo"
              density="compact"
              class="group-parent-select"
              @update:model-value="groupListChanged(item)"
              @blur="parentSelected(item)"
            >
              <template #item="data">
                <v-list-item
                  v-bind="data.props"
                  :id="`client-individual-modal--set-parent-for-selected-group--option--${String(
                    data.item.group
                  ).replaceAll(' ', '')}`"
                >
                  {{ data.item.group }}
                </v-list-item>
              </template>
            </v-autocomplete>
            <div v-else class="parent-col">
              <a
                :id="getStudentManagementParentSelectionId(item.group)"
                class="group-parent-link"
                :disabled="groupsLoading"
                @click="displayParentSelection(item)"
              >
                {{
                  !item.parent
                    ? '[select]'
                    : getParentGroupName(item.parent).length === 0
                      ? '[missing parent]'
                      : getParentGroupName(item.parent)
                }}
              </a>
              <span v-if="vendorGroupIdDisplayEnabled && item.parent" class="studentGroupId">
                [{{ getParentGroupId(item.parent) }}]</span
              >
            </div>
          </template>
          <template #[`item.subjectGroup`]="{ item }">
            <v-checkbox-btn
              :id="getStudentManagementSubjectGroupCheckboxId(item.group)"
              v-model="item.subjectGroup"
              class="subject-group-checkbox"
              color="primary"
              :disabled="groupsLoading"
              @update:model-value="groupListChanged(item)"
            ></v-checkbox-btn>
          </template>
          <template #[`item.customGroup`]="{ item }">
            <v-checkbox-btn
              :id="getStudentManagementCustomGroupCheckboxId(item.group)"
              v-model="item.customGroup"
              class="custom-group-checkbox"
              color="primary"
              :disabled="groupsLoading"
              @update:model-value="groupListChanged(item)"
            ></v-checkbox-btn>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </div>
</template>
<script>
import { CSBase } from '@complispace/cs-design-system';
import StudentGroupSyncDiffAlert from './StudentGroupSyncDiffAlert';
import studentGroupTableColumn from '../lib/const/studentGroupTableColumn';
import cloneDeep from 'lodash/cloneDeep';

export default {
  name: 'ClientStudentManagementGroup',

  components: {
    StudentGroupSyncDiffAlert
  },

  extends: CSBase,

  props: {
    hideSelected: {
      type: Boolean,
      default: false
    },
    parentSelectionList: {
      type: Array,
      required: true
    },
    groupList: {
      type: Array,
      required: true
    },
    groupsLoading: {
      type: Boolean,
      required: false,
      default: false
    },
    pagination: {
      type: Object,
      default: () => {
        return {
          itemsPerPage: 30,
          page: 1,
          sortBy: []
        };
      }
    },
    studentManagementGroupSyncDiffs: {
      type: Array,
      required: true
    },
    schoolTypes: {
      type: Array,
      required: true
    },
    vendorGroupIdDisplayEnabled: {
      type: Boolean,
      default: true
    },
    searchTerm: {
      type: String,
      required: false,
      default: ''
    }
  },
  emits: ['update:searchTerm', 'update:groupList', 'update:pagination'],
  data() {
    return {
      selectedGroupItems: [],
      selectedApplyParentId: null,
      applySubjectEnabled: false,
      applyCustomEnabled: false,
      searchGroup: '',
      groupHeaders: [
        {
          title: studentGroupTableColumn.GROUP.label,
          value: studentGroupTableColumn.GROUP.tableColumnRef,
          headerProps: { class: 'table-header' },
          sortable: true,
          width: 200
        },
        {
          title: studentGroupTableColumn.SCHOOL_TYPE.label,
          value: studentGroupTableColumn.SCHOOL_TYPE.tableColumnRef,
          headerProps: { class: 'table-header' },
          sortable: true,
          width: 200
        },
        {
          title: studentGroupTableColumn.PARENT.label,
          value: studentGroupTableColumn.PARENT.tableColumnRef,
          headerProps: { class: 'table-header' },
          sortable: true,
          width: 200
        },
        {
          title: studentGroupTableColumn.SUBJECT_GROUP.label,
          value: studentGroupTableColumn.SUBJECT_GROUP.tableColumnRef,
          headerProps: { class: 'table-header' },
          sortable: true,
          width: 110
        },
        {
          title: studentGroupTableColumn.CUSTOM_GROUP.label,
          value: studentGroupTableColumn.CUSTOM_GROUP.tableColumnRef,
          headerProps: { class: 'table-header' },
          sortable: true,
          width: 110
        }
      ],
      studentGroupPaginationInfo: {
        itemsPerPage: 30,
        page: 1,
        sortBy: []
      },
      editableGroupList: this.groupList
    };
  },
  computed: {
    applyButtonDisabled() {
      return !this.selectedGroupItems || this.selectedGroupItems.length === 0;
    },
    filterActivityRisks() {
      return (type, riskClass) => {
        const typeName = type ? type.name : '';
        const className = riskClass ? riskClass.name : '';
        return this.$store.getters['common/getActivityRisksByClass'](typeName, className);
      };
    }
  },
  watch: {
    groupList() {
      this.setEditableGroupList();
    },
    searchTerm() {
      this.searchGroup = this.searchTerm;
      this.filterGroups();
    },
    studentGroupPaginationInfo: {
      handler(val) {
        this.paginationChanged(val);
      },
      deep: true
    },
    pagination() {
      this.initPagination();
    }
  },
  mounted() {
    this.searchGroup = this.searchTerm;
    this.editableGroupList = cloneDeep(this.groupList);
    this.initPagination();
  },
  methods: {
    getStudentManagementCheckboxId(group) {
      return `client-individual-modal--student-management--checkbox-${group}`;
    },
    getStudentManagementParentSelectionId(group) {
      return `client-individual-modal--student-management-parent--selection-${group}`;
    },
    getStudentManagementSubjectGroupCheckboxId(group) {
      return `client-individual-modal--student-management-subject-group--checkbox-${group}`;
    },
    getStudentManagementCustomGroupCheckboxId(group) {
      return `client-individual-modal--student-management-custom-group--checkbox-${group}`;
    },

    initPagination() {
      const { itemsPerPage, page, sortBy } = this.pagination;
      this.studentGroupPaginationInfo = {
        itemsPerPage: itemsPerPage || 30,
        page: page || 1,
        sortBy: sortBy || []
      };
    },
    clearSearchInput() {
      this.searchGroup = '';
      this.editableGroupList = cloneDeep(this.groupList);
      this.searchGroupChanged('');
    },
    searchGroupChanged(newSearchGroup) {
      this.$emit('update:searchTerm', newSearchGroup);
    },
    getSelectedSchoolType(item) {
      if (!item.school || !this.schoolTypes) {
        return undefined;
      }
      const validSchool = this.schoolTypes.find(
        (schoolType) => String(item.school) === String(schoolType.id)
      );
      if (!validSchool) {
        this.schoolTypeChanged(item, undefined);
      }
      return validSchool ? validSchool.id : undefined;
    },
    schoolTypeChanged(item, value) {
      item.school = value;
      this.groupListChanged(item);
    },
    groupListChanged(item) {
      const itemId = item.id;
      const newGroupList = cloneDeep(this.groupList);
      const targetIndex = newGroupList.findIndex((item) => item.id === itemId);
      newGroupList[targetIndex] = { ...item };
      this.$emit('update:groupList', newGroupList);
    },
    paginationChanged(newPagination) {
      this.$emit('update:pagination', newPagination);
    },
    resetStudentGroupPaginationInfo() {
      this.studentGroupPaginationInfo = {
        itemsPerPage: 30,
        page: 1,
        sortBy: []
      };
      this.paginationChanged(this.studentGroupPaginationInfo);
    },
    filterGroups() {
      this.resetStudentGroupPaginationInfo();
      const parsedSearchTerm = String(this.searchGroup).trim().toLowerCase();
      if (this.searchGroup && parsedSearchTerm.length !== 0) {
        this.setEditableGroupList();
        this.searchGroupChanged(this.searchGroup);
        this.$forceUpdate();
      } else {
        this.clearSearchInput();
      }
    },
    setEditableGroupList() {
      const parsedSearchTerm = String(this.searchGroup).trim().toLowerCase();
      let newGroupList = [];
      if (this.searchGroup && parsedSearchTerm.length !== 0) {
        newGroupList = this.groupList.filter((item) =>
          String(item.group).toLowerCase().includes(parsedSearchTerm)
        );
        this.editableGroupList = newGroupList;
      } else {
        this.editableGroupList = cloneDeep(this.groupList);
      }
    },
    getParentGroupName(parentId) {
      const parentGroup = this.parentSelectionList.find((g) => `${g.id}` === `${parentId}`);
      return parentGroup ? parentGroup.group : '';
    },
    getParentGroupId(parentId) {
      const parentGroup = this.parentSelectionList.find((g) => `${g.id}` === `${parentId}`);
      return parentGroup ? parentGroup.id : '';
    },
    displayParentSelection(item) {
      this.editableGroupList.forEach((g) => {
        g.parentSelectionDisplayed = false;
        if (g.id === item.id) {
          g.parentSelectionDisplayed = true;
        }
      });
    },
    applySelectedOption() {
      const newGroupList = cloneDeep(this.groupList);
      const { selectedApplyParentId } = this;
      if (this.selectedGroupItems && this.selectedGroupItems.length > 0) {
        this.selectedGroupItems.forEach(async (group) => {
          const localGroup = group;
          if (this.applySubjectEnabled) {
            localGroup.subjectGroup = true;
          }
          if (this.applyCustomEnabled) {
            localGroup.customGroup = true;
          }
          localGroup.parent = selectedApplyParentId;
          const targetIndex = newGroupList.findIndex((item) => item.id === localGroup.id);
          newGroupList[targetIndex] = { ...localGroup };
        });
        this.$emit('update:groupList', newGroupList);
      }
      this.selectedGroupItems = [];
      this.selectedApplyParentId = null;
      this.applySubjectEnabled = false;
      this.applyCustomEnabled = false;
      this.triggerAutocompleteClear();
    },
    parentSelected(item) {
      const localItem = item;
      localItem.parentSelectionDisplayed = false;
      this.groupListChanged(item);
    },
    // alternative fix for clearing autocomplete
    // as v-autocomplete clear button is not working when the v-model is empty
    triggerAutocompleteClear() {
      const autocomplete = this.$refs.parentSelection;
      if (autocomplete) {
        const clearButton = autocomplete.$el.querySelector('.v-field__clearable .v-icon');
        if (clearButton) {
          clearButton.click();
        }
      }
    }
  }
};
</script>
<style scoped>
.parent-selection-col {
  padding-top: 8px !important;
}

.group-parent-link {
  color: rgb(var(--v-theme-primary)) !important;
  cursor: pointer;
}

.search-group-textfield :deep(.v-field__input) {
  padding-bottom: 4px !important;
}
</style>
